import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  login as apiLogin,
  register as apiRegister,
  googleLogin as apiGoogleLogin,
  getCurrentUser,
  logout as apiLogout,
  User
} from '../services/auth';
import { AUTH_EXPIRED_EVENT } from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  googleLogin: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Optimistic hint of "this browser had an authenticated session", so the app can show a
// loading state (instead of flashing the landing page) while /auth/me is in flight on boot.
export const AUTH_SESSION_HINT_KEY = 'entire:hadSession';
export const hadAuthSessionHint = () => localStorage.getItem(AUTH_SESSION_HINT_KEY) === '1';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getCurrentUser()
      .then(setUser)
      .finally(() => setIsLoading(false));
  }, []);

  // Keep the optimistic session hint in sync with the resolved auth state. Only update it
  // once the initial check has finished, so the boot-time loader can still read the prior value.
  useEffect(() => {
    if (isLoading) return;
    if (user) localStorage.setItem(AUTH_SESSION_HINT_KEY, '1');
    else localStorage.removeItem(AUTH_SESSION_HINT_KEY);
  }, [user, isLoading]);

  useEffect(() => {
    const handleAuthExpired = () => setUser(null);
    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
  }, []);

  const login = async (email: string, password: string) => {
    const userData = await apiLogin(email, password);
    setUser(userData);
  };

  const register = async (name: string, email: string, password: string) => {
    const userData = await apiRegister(name, email, password);
    setUser(userData);
  };

  const googleLogin = async (idToken: string) => {
    const userData = await apiGoogleLogin(idToken);
    setUser(userData);
  };

  // Clear local auth state even if the logout request fails, so the user is always signed out client-side.
  const logout = async () => {
    try {
      await apiLogout();
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      register,
      googleLogin,
      logout,
      setUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
